/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React from 'react';
import { observer } from 'mobx-react';
import { getEnv } from 'mobx-state-tree';

import PropTypes from 'util/PropTypes';
import { performContentSelection } from 'util/SelectionUtil';

import { ReactLayoutImplBase } from '../base/LayoutBase';
import { ExploreVisWidget } from './ExploreVisWidget';

import Measure from 'react-measure';
import classnames from 'classnames';

import './exploreCard.scss';

const LIVE_WIDGET = 'widget.live';
const PLACEHOLDER = 'widget.placeholder';
/**
 * Class representing view implementation (using react) for ExploreCard layout view
 * @extends ReactLayoutImplBase
 */
@observer
export class ExploreCardView extends ReactLayoutImplBase {
	constructor(props) {
		super(props);
		this.footerView = this.props.footerView;
		this.boardModel = this.props.layoutController.boardModel;
		this._childrenRenderMarkupAdded = this.model.childrenRenderMarkupAdded || 0;

		//TODO _alive - VisTypeChooser refers to layoutOwner where object might not be available, introduce this flag to indicate if the view is destroyed or not. perhaps we can use a more proper way to address this
		this._alive = true;
		this.listeners = [];
		this.boardModel._hookupCustomDataHandler = () => this.props.model.cometViewModel.hookupCustomDataHandler();
	}

	state = {
		dimensions: {
			width: -1,
			height: -1
		}
	}

	componentDidMount() {
		this.props.model.cometViewModel.hookupCustomDataHandler();
		// TODO we should listen on content api event, but our code currently are directly using the boardModel method
		// to add or remove widgets, we need to clean up our code to use content api to add or remove widgets.
		this.listeners.push(this.boardModel.on('addWidget', this._onAddWidget.bind(this)) );
		this.listeners.push(this.boardModel.on('addLayouts', this._onAddLayout.bind(this)) );
	}

	componentWillUnmount() {
		this._removeListeners();
	}

	/**
	 * handle selection when
	 *  - adding a data widget in a card by `addWidget`
	 *  - undo adding a data widget in a card, a placeholder is added by `_onAddWidget`
	 * @param {object} payload 
	 */
	_onAddWidget(payload) {
		if (payload.value) {
			const id = payload.value.parameter.model.id;
			const typesToSelect = [LIVE_WIDGET];
			// if is undoing, then we also select for placecholder
			if(payload.sender === 'UndoRedoController') {
				typesToSelect.push(PLACEHOLDER);
			}
			return this._doContentSelection(id, typesToSelect);
		}
		return Promise.resolve();
	}

	/**
	 * handle selection when
	 *  - adding a data widget in a card by `addLayouts`
	 *  - undo adding a data widget in a card, a placeholder is added by `addLayouts`
	 * @param {object} payload 
	 */
	_onAddLayout(payload) {
		if (payload.value) {
			const addLayoutArray = payload.value.parameter.addLayoutArray;
			if(addLayoutArray && addLayoutArray.length === 1) {
				const id = addLayoutArray[0].model.id;
				const typesToSelect = [LIVE_WIDGET];
				// if is undoing, then we also select for placecholder
				if(payload.sender === 'UndoRedoController') {
					typesToSelect.push(PLACEHOLDER);
				}
				return this._doContentSelection(id, typesToSelect);
			}
		}
		return Promise.resolve();
	}

	_doContentSelection(id, typesToSelect) {
		if(id && this.model.findModel(id)) {
			const dashboardApi = this._getDashboardApi();
			const canvas = dashboardApi && dashboardApi.getFeature('Canvas');
			if (canvas) {
				const content = canvas.getContent(id);
				if(content && (typesToSelect.indexOf(content.getType()) !== -1)) {
					const state = canvas.getContent(id).getFeature('state');
					return state.whenStatusChanges(state.STATUS.RENDERED).then(() => {
						performContentSelection(dashboardApi, [id]);
					});
				}
			}
		}
		return Promise.resolve();
	}

	_getDashboardApi() {
		return getEnv(this.props.model).dashboardApi;
	}

	_removeListeners() {
		this.listeners.forEach( (entry) =>{
			entry.remove();
		});
	}

	render() {
		return this.getRenderMarkup();
	}

	renderContent() {
		let numWidgets = 0;
		this.layoutOwner = this.props.layoutOwner; //should always use props, still need this.layoutOwner to support getChildrenRenderMarkup()

		const items = this.props.model.getVisualizations().map(
			(o) => {
				if( o.widgetLayout) {
					numWidgets++;
				}
				const id = o.widgetLayout ? o.widgetLayout.id : o.placeholder.id;
				const headerMap = this.props.getWidgetHeaderMap && this.props.getWidgetHeaderMap();
				return (
					<ExploreVisWidget
						dashboardApi={this.layoutController.dashboardApi}
						header={headerMap && headerMap[id] || null}
						renderPopOver={this.props.renderPopOver}
						getCrossHairMatchingPoints={this.props.getCrossHairMatchingPoints}
						key={id} model={o}
						layoutOwner={this.layoutOwner}
						onDragEnd={()=>{}}
					>
					</ExploreVisWidget>
				);
			}
		);

		const classNamesMain = this.props.layoutClasses().classNamesMain;
		const classNamesCardRow = this.props.layoutClasses().classNamesCardRow;
		const { width, height } = this.state.dimensions;

		const classes = classnames(
			classNamesCardRow,
			{
				[`${classNamesCardRow}__vertical`]: (height > width)
			});

		return (
			<div className="card-layout-content">
				<div className={classNamesMain}>
					<Measure
						bounds
						onResize={this.onResize}
					>
						{({ measureRef }) => (
							<div ref={measureRef} className={classes}>
								{items}
							</div>
						)}
					</Measure>
					{numWidgets > 0 && this.props.footerView}
				</div>
			</div>
		);
	}

	onResize = (contentRect) => {
		if( contentRect.bounds.height !== 0 && contentRect.bounds.width !== 0) {
			this.setState({ dimensions: contentRect.bounds });
		}
	}

	getRenderMarkup() {
		return (
			<div className="card-layout">
				{this.renderContent()}
			</div>
		);
	}

	trackWidgetDropTarget(target) {
		this.parentLayout.trackWidgetDropTarget(target);
	}
}


ExploreCardView.propTypes = {
	items: PropTypes.array,
	layoutController: PropTypes.object,
	services: PropTypes.object,
	parentLayout: PropTypes.object,
	model: PropTypes.object,
	layoutOwner: PropTypes.object,
	getWidgetHeaderMap: PropTypes.func,
	getOverview: PropTypes.func,
	footerView: PropTypes.object,
	renderPopOver: PropTypes.func,
	getCrossHairMatchingPoints: PropTypes.func
};



// WEBPACK FOOTER //
// ./src/js/layout/explorecard/ExploreCardView.js